/**
 * Global Fetch Interceptor
 * Intercepts all fetch requests to handle 401/403 errors and auto-logout
 */
import logger from './logger';

let logoutHandler = null;
let isHandlingAuthError = false;
let auth0IsLoading = true; // Auth0 starts in loading state on every page load

const getRequestUrl = (input) => {
  if (typeof input === 'string') return input;
  if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
  return typeof input?.url === 'string' ? input.url : '';
};

// Tenant identity is a separate security boundary. Its expected anonymous,
// expired, or revoked session responses are handled inside TenantIdentityContext
// and must never clear or redirect an otherwise valid admin Auth0 session.
export const isTenantScopedEndpoint = (input) => {
  const url = getRequestUrl(input);
  return url.includes('/api/auth/tenant/') || url.includes('/api/tenant/');
};

/**
 * Set the logout handler function
 * @param {Function} handler - Auth0 logout function
 */
export const setLogoutHandler = (handler) => {
  logoutHandler = handler;
};

/**
 * Signal whether Auth0 is still initializing.
 * The interceptor will not trigger logout while Auth0 is loading,
 * preventing race-condition logouts on page refresh.
 * @param {boolean} isLoading
 */
export const setAuth0LoadingState = (isLoading) => {
  auth0IsLoading = isLoading;
};

/**
 * Handle authentication errors and trigger auto-logout
 */
const handleAuthError = async (response) => {
  if (isHandlingAuthError) {
    return; // Prevent multiple simultaneous logout attempts
  }

  // Don't fire during Auth0 initialization — it produces 401s while the SDK
  // is still restoring the session from localStorage, and firing here would
  // clear that localStorage data and lock the user out on every page refresh.
  if (auth0IsLoading) {
    logger.debug('Auth0 still loading, skipping auto-logout for 401', { component: 'FetchInterceptor' });
    return;
  }

  // Only handle 401 (Unauthorized) errors
  if (response.status === 401) {
    isHandlingAuthError = true;
    logger.warn('401 Unauthorized detected, triggering auto-logout', { component: 'FetchInterceptor' });

    try {
      // Clear Auth0 cache
      const auth0Keys = Object.keys(localStorage).filter((key) => key.includes('auth0'));
      auth0Keys.forEach((key) => {
        localStorage.removeItem(key);
      });

      // Clear sessionStorage Auth0 data
      Object.keys(sessionStorage).forEach((key) => {
        if (key.includes('auth0')) {
          sessionStorage.removeItem(key);
        }
      });

      // Call logout handler if available
      if (logoutHandler && typeof logoutHandler === 'function') {
        await logoutHandler({
          logoutParams: {
            returnTo: window.location.origin
          }
        });
      } else {
        // Fallback: redirect to home page
        logger.warn('No logout handler set, redirecting to home', { component: 'FetchInterceptor' });
        window.location.href = '/';
      }
    } catch (error) {
      logger.error('Error during auto-logout', { component: 'FetchInterceptor', error: error.message });
      // Force redirect as fallback
      window.location.href = '/';
    } finally {
      isHandlingAuthError = false;
    }
  }
};

/**
 * Setup global fetch interceptor
 * This wraps the native fetch function to intercept responses
 */
export const setupGlobalFetchInterceptor = () => {
  // Store original fetch
  const originalFetch = window.fetch;

  // Override fetch
  window.fetch = async function(...args) {
    // Call original fetch
    const response = await originalFetch.apply(this, args);

    // Clone response so we can read it without consuming the stream
    const clonedResponse = response.clone();

    // Check for 401 errors (but only for API calls, not static assets)
    if (!response.ok && (response.status === 401 || response.status === 403)) {
      const url = getRequestUrl(args[0]);
      
      // Only handle API endpoints, not static assets
      // Exclude endpoints that use 401/403 for business-logic errors, not session expiry:
      // - PIN verification: 401/403 = invalid PIN
      // - OTP verification: 401 = invalid/expired code
      const isExcluded = typeof url === 'string' && (
        url.includes('/api/pin/') ||
        url.includes('/api/bookings/verify-otp') ||
        url.includes('/api/bookings/auth/fallback') ||
        isTenantScopedEndpoint(url)
      );
      if (typeof url === 'string' && (url.includes('/api/') || url.startsWith('/api/')) && !isExcluded) {
        // Read response to check error details
        try {
          const errorData = await clonedResponse.json().catch(() => ({}));
          
          // Check if it's a token expiration error
          if (
            response.status === 401 &&
            (errorData.error === 'Token expired' ||
             errorData.message?.includes('expired') ||
             errorData.message?.includes('login again'))
          ) {
            logger.warn('Token expired error detected', { component: 'FetchInterceptor' });
            await handleAuthError(response);
          } else if (response.status === 401) {
            // Other 401 errors
            logger.warn('Unauthorized error detected', { component: 'FetchInterceptor' });
            await handleAuthError(response);
          }
        } catch (error) {
          // If we can't parse the response, still handle 401
          if (response.status === 401) {
            await handleAuthError(response);
          }
        }
      }
    }

    return response;
  };

  logger.debug('Global fetch interceptor setup complete', { component: 'FetchInterceptor' });
};
